import AdminPromos from "./AdminPromos";
import React, { useState, useEffect, useRef, useMemo } from "react";
import API from "../api";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
  PieChart, Pie, Cell 
} from 'recharts';

const NOTIFICATION_SOUND = "https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3";

const Admin = () => {
  const navigate = useNavigate();
  const audioRef = useRef(new Audio(NOTIFICATION_SOUND));

  // 🟢 NEW: Notification Refs & State
  const prevOrderCountRef = useRef(0);
  const titleBlinkIntervalRef = useRef(null);
  const [unreadOrders, setUnreadOrders] = useState(0);

  const [activeTab, setActiveTab] = useState("dashboard");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [banners, setBanners] = useState([]);
  const [combos, setCombos] = useState([]); 
  const [loading, setLoading] = useState(true);
  
  const [isStoreOpen, setIsStoreOpen] = useState(true);

  const [orderFilter, setOrderFilter] = useState("All"); 
  const [selectedDate, setSelectedDate] = useState(""); 
  
  const [newBannerImage, setNewBannerImage] = useState(null);
  const [newBannerLink, setNewBannerLink] = useState("All"); 
  
  const [productSearch, setProductSearch] = useState("");
  
  const [showLowStock, setShowLowStock] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  
  // Bulk Category State
  const [bulkCategory, setBulkCategory] = useState("");

  const [showProductModal, setShowProductModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [newCategory, setNewCategory] = useState("");
  
  // 🟢 AI Description included safely
  const [form, setForm] = useState({ 
    name: "", description: "", price: "", mrp: "", costPrice: "", expiryDate: "", stock: "", category: "", isBestSeller: false, isVisible: true, image: null 
  });

  const [showComboModal, setShowComboModal] = useState(false);
  const [comboForm, setComboForm] = useState({ name: "", description: "", comboPrice: "", image: null, items: [] });
  const [comboSelectedProduct, setComboSelectedProduct] = useState("");
  const [comboSelectedQty, setComboSelectedQty] = useState(1);

  // Category Edit State
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState("");

  // 🟢 NEW: Auto-Clear Notifications when clicking the Orders tab
  useEffect(() => {
    if (activeTab === "orders") {
      setUnreadOrders(0);
      if (titleBlinkIntervalRef.current) {
        clearInterval(titleBlinkIntervalRef.current);
        titleBlinkIntervalRef.current = null;
      }
      document.title = "DelightAdmin";
    }
  }, [activeTab]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) { navigate("/admin-login"); return; }

      const [pRes, oRes, cRes, bRes, sRes, comboRes] = await Promise.all([
        API.get("/products?limit=1000"), 
        API.get("/orders"),
        API.get("/categories").catch(() => ({ data: [] })),
        API.get("/banners").catch(() => ({ data: [] })),
        API.get("/settings/status").catch(() => ({ data: { isOpen: true } })),
        API.get("/combos/admin").catch(() => ({ data: [] })) 
      ]);

      setProducts(Array.isArray(pRes.data.products) ? pRes.data.products : pRes.data || []);
      setCombos(comboRes.data || []);
      
      const sortedOrders = Array.isArray(oRes.data) ? oRes.data.sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt)) : [];
      
      // 🟢 UPGRADED: Rock Solid Notification Logic
      if (prevOrderCountRef.current > 0 && sortedOrders.length > prevOrderCountRef.current) {
         const newOrdersCount = sortedOrders.length - prevOrderCountRef.current;
         
         // 1. Update Red WhatsApp Badge
         if (activeTab !== "orders") {
           setUnreadOrders(prev => prev + newOrdersCount);
         }

         // 2. Play Sound (Safely catches browser auto-play blocks)
         toast.success(`🔔 ${newOrdersCount} New Order(s) Received!`, { icon: "📦", duration: 6000 });
         const playPromise = audioRef.current.play();
         if (playPromise !== undefined) {
           playPromise.catch(error => console.log("Audio blocked by browser. Click anywhere on the page to allow audio."));
         }

         // 3. Start Blinking Browser Tab
         if (!titleBlinkIntervalRef.current && activeTab !== "orders") {
           let isAltTitle = false;
           titleBlinkIntervalRef.current = setInterval(() => {
             document.title = isAltTitle ? `(🔴 ${newOrdersCount}) New Order!` : "DelightAdmin";
             isAltTitle = !isAltTitle;
           }, 1000);
         }
      }
      // Update memory ref for next tick
      prevOrderCountRef.current = sortedOrders.length;

      setOrders(sortedOrders);
      setCategories(Array.isArray(cRes.data) ? cRes.data : []);
      setBanners(bRes.data);
      setIsStoreOpen(sRes.data.isOpen); 

      setLoading(false);
    } catch (err) {
      console.error("Admin Load Error:", err);
      navigate("/admin-login");
    }
  };

  useEffect(() => { 
    fetchData(); 
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [navigate]);

  const totalRevenue = orders.filter(o => o.status !== "Cancelled").reduce((sum, o) => sum + o.total, 0);
  const pendingOrdersCount = orders.filter(o => o.status === "Pending").length;
  const lowStockCount = products.filter(p => p.stock < 5).length;

  const salesData = orders.slice(0, 10).map(o => ({ name: o.name.split(" ")[0], amount: o.total })).reverse();

  const categoryData = [];
  products.forEach(p => {
    const existing = categoryData.find(c => c.name === p.category);
    if (existing) existing.value += 1;
    else categoryData.push({ name: p.category, value: 1 });
  });
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

  const aiInsights = useMemo(() => {
    const now = new Date();
    const insights = { expiryAlerts: [], priceCorrections: [], trending: [] };

    products.forEach(p => {
      if (p.expiryDate && p.stock > 0) {
        const daysToExpiry = Math.ceil((new Date(p.expiryDate) - now) / (1000 * 60 * 60 * 24));
        if (daysToExpiry <= 10 && daysToExpiry >= 0) {
          insights.expiryAlerts.push({ ...p, daysToExpiry });
        }
      }

      const views = p.viewCount || 0;
      const sales = p.soldCount || 0;
      if (views >= 15 && p.stock > 0) {
        const conversionRate = sales / views;
        if (conversionRate <= 0.05) {
          const margin = p.price - (p.costPrice || 0);
          let suggestedPrice = Math.floor(p.price * 0.9);
          if (p.costPrice && suggestedPrice < p.costPrice) suggestedPrice = p.costPrice;
          
          insights.priceCorrections.push({ ...p, conversionRate, suggestedPrice, margin });
        }
      }

      if (sales > 10) {
        insights.trending.push(p);
      }
    });

    insights.priceCorrections.sort((a, b) => a.conversionRate - b.conversionRate);
    insights.trending.sort((a, b) => (b.soldCount || 0) - (a.soldCount || 0));

    return insights;
  }, [products]);

  const toggleStoreStatus = async () => {
    try {
      const newStatus = !isStoreOpen;
      setIsStoreOpen(newStatus); 
      await API.post("/settings/status", { isOpen: newStatus });
      toast.success(newStatus ? "🟢 Store is now OPEN" : "🔴 Store is now CLOSED");
    } catch (err) {
      toast.error("Failed to update status");
      setIsStoreOpen(!isStoreOpen); 
    }
  };

  const toggleVisibility = async (id) => {
    try {
      await API.patch(`/products/${id}/visibility`);
      toast.success("Visibility Updated");
      fetchData();
    } catch (err) { toast.error("Update failed"); }
  };

  const openProductModal = (product = null) => {
    if (product) {
      setEditId(product._id);
      setForm({
        name: product.name, description: product.description || "", price: product.price, mrp: product.mrp || "", costPrice: product.costPrice || "",
        stock: product.stock || "", category: product.category, isBestSeller: product.isBestSeller === true,
        isVisible: product.isVisible !== false, expiryDate: product.expiryDate ? new Date(product.expiryDate).toISOString().split('T')[0] : "",
        image: null
      });
    } else {
      setEditId(null);
      setForm({ name: "", description: "", price: "", mrp: "", costPrice: "", stock: "", category: "", isBestSeller: false, isVisible: true, expiryDate: "", image: null });
    }
    setShowProductModal(true);
  };

  const handleGenerateDescription = async () => {
    if (!form.name) return toast.error("Please enter a product name first!");
    const toastId = toast.loading("🪄 AI is writing description...");
    try {
      const res = await API.post("/ai/generate-description", { productName: form.name });
      setForm({ ...form, description: res.data.description });
      toast.success("Description generated!", { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate. Check API key.", { id: toastId });
    }
  };

  const handleSubmitProduct = async (e) => {
    e.preventDefault();
    if (!form.category) return toast.error("Category is required!");
    const formData = new FormData();
    Object.keys(form).forEach(key => {
      if (key === 'image' && !form.image) return;
      formData.append(key, form[key]);
    });
    try {
      if (editId) {
        await API.put(`/products/${editId}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Product Updated");
      } else {
        if (!form.image) return toast.error("Image required");
        await API.post("/products", formData, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Product Added");
      }
      fetchData();
      setShowProductModal(false);
    } catch (err) { toast.error("Operation Failed"); }
  };

  const handleMarkOutOfStock = async (id) => {
    if(!window.confirm("Mark Sold Out immediately?")) return;
    try { await API.put(`/products/${id}`, { stock: 0 }); toast.success("Stock Updated!"); fetchData(); } catch(e){}
  };

  const handleDeleteProduct = async (id) => {
    if(!window.confirm("Delete permanently?")) return;
    try { await API.delete(`/products/${id}`); toast.success("Deleted"); fetchData(); } catch(e){}
  };

  const getFilteredProducts = () => {
    let filtered = products;
    if (showLowStock) filtered = filtered.filter(p => p.stock < 5);
    if (productSearch) filtered = filtered.filter(p => p.name.toLowerCase().includes(productSearch.toLowerCase()) || p.category.toLowerCase().includes(productSearch.toLowerCase()));
    return filtered;
  };
  const displayedProducts = getFilteredProducts();

  const handleAddComboItem = () => {
    if (!comboSelectedProduct || comboSelectedQty < 1) return;
    const productData = products.find(p => p._id === comboSelectedProduct);
    if (!productData) return;

    const existsIndex = comboForm.items.findIndex(i => i.product === comboSelectedProduct);
    let newItems = [...comboForm.items];
    
    if (existsIndex >= 0) {
      newItems[existsIndex].quantity += parseInt(comboSelectedQty);
    } else {
      // 🟢 FIXED: Now it captures the MRP as well as the Selling Price
      newItems.push({ 
        product: comboSelectedProduct, 
        name: productData.name, 
        price: productData.price, 
        mrp: productData.mrp || productData.price, // Fallback to price if MRP is missing
        quantity: parseInt(comboSelectedQty) 
      });
    }
    
    setComboForm({ ...comboForm, items: newItems });
    setComboSelectedProduct("");
    setComboSelectedQty(1);
  };

  const removeComboItem = (index) => {
    const newItems = comboForm.items.filter((_, i) => i !== index);
    setComboForm({ ...comboForm, items: newItems });
  };

  // 🟢 FIXED: Calculate Total MRP for the psychological anchor
  const calculatedMRP = comboForm.items.reduce((sum, item) => sum + ((item.mrp || item.price) * item.quantity), 0);
  
  // Calculate what they would pay if they just bought them normally from your store
  const calculatedSellingPrice = comboForm.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  const handleSubmitCombo = async (e) => {
    e.preventDefault();
    
    // 1. Strict Frontend Validation
    if (!comboForm.name.trim()) return toast.error("Combo Name is required!");
    if (!comboForm.comboPrice) return toast.error("Selling Price is required!");
    if (comboForm.items.length === 0) return toast.error("Please add at least 1 product to the combo!");
    if (!comboForm.image) return toast.error("Combo Cover Image is required!");

    const formData = new FormData();
    formData.append("name", comboForm.name);
    formData.append("description", comboForm.description || "");
    formData.append("originalPrice", Number(calculatedMRP)); // Force it to be a clean number
    formData.append("comboPrice", Number(comboForm.comboPrice)); // Force it to be a clean number
    formData.append("image", comboForm.image);
    
    const cleanItems = comboForm.items.map(i => ({ product: i.product, quantity: i.quantity }));
    formData.append("items", JSON.stringify(cleanItems));

    const toastId = toast.loading("Creating Combo...");
    try {
      await API.post("/combos", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Combo created successfully!", { id: toastId });
      setShowComboModal(false);
      setComboForm({ name: "", description: "", comboPrice: "", image: null, items: [] });
      fetchData();
    } catch (err) {
      console.error("Combo Creation Error:", err);
      // 2. 🟢 SMART ERROR READING: Tell us exactly what the backend didn't like!
      const errorMessage = err.response?.data?.error || err.response?.data?.message || "Failed to create combo";
      toast.error(errorMessage, { id: toastId });
    }
  };

  const toggleComboVisibility = async (id) => {
    try {
      await API.patch(`/combos/${id}/visibility`);
      toast.success("Combo visibility updated");
      fetchData();
    } catch (err) { toast.error("Update failed"); }
  };

  const handleDeleteCombo = async (id) => {
    if(!window.confirm("Delete this Combo permanently?")) return;
    try { await API.delete(`/combos/${id}`); toast.success("Combo Deleted"); fetchData(); } catch(e){}
  };

  const handleExportCSV = () => {
    const headers = ["ID", "Name", "Category", "CostPrice", "MRP", "Price", "Stock", "SoldCount", "ViewCount", "ExpiryDate"];
    const csvRows = [headers.join(",")];
    products.forEach(p => {
      const row = [ p._id, `"${p.name.replace(/"/g, '""')}"`, `"${p.category}"`, p.costPrice || 0, p.mrp, p.price, p.stock, p.soldCount || 0, p.viewCount || 0, p.expiryDate ? p.expiryDate.split('T')[0] : "" ];
      csvRows.push(row.join(","));
    });
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.setAttribute("hidden", ""); a.setAttribute("href", url); a.setAttribute("download", `delight_inventory.csv`);
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    toast.success("CSV Exported Successfully!", { icon: "📥" });
  };

  const handleBulkAction = async (actionType) => {
    if (actionType === 'changeCategory' && !bulkCategory) return toast.error("Please select a category first!");
    if (!window.confirm(`Perform this action on ${selectedProducts.length} items?`)) return;
    
    const toastId = toast.loading("Processing bulk action...");
    try {
      const promises = selectedProducts.map(id => {
        if (actionType === 'outOfStock') return API.put(`/products/${id}`, { stock: 0 });
        if (actionType === 'toggleVisibility') return API.patch(`/products/${id}/visibility`);
        if (actionType === 'delete') return API.delete(`/products/${id}`);
        if (actionType === 'changeCategory') return API.put(`/products/${id}`, { category: bulkCategory }); 
        return Promise.resolve();
      });
      
      await Promise.all(promises);
      toast.success(`Successfully updated ${selectedProducts.length} items!`, { id: toastId });
      setSelectedProducts([]); 
      setBulkCategory(""); 
      fetchData();
    } catch (err) { 
      toast.error("Some items failed to update.", { id: toastId }); 
      fetchData(); 
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) return;
    try { await API.post("/categories", { name: newCategory }); toast.success("Category Added"); setNewCategory(""); fetchData(); } catch(e){}
  };

  const handleDeleteCategory = async (id) => {
    if(!window.confirm("Delete Category? Warning: Products inside this category will still exist but lack a valid category.")) return;
    try { await API.delete(`/categories/${id}`); toast.success("Deleted"); fetchData(); } catch(e){}
  };

  const submitEditCategory = async (e) => {
    e.preventDefault();
    if (!editingCategoryName.trim()) return;
    
    const toastId = toast.loading("Renaming category & migrating products...");
    try {
      const res = await API.put(`/categories/${editingCategory}`, { newName: editingCategoryName });
      toast.success(`${res.data.productsMigrated} products moved to new category!`, { id: toastId });
      setEditingCategory(null);
      setEditingCategoryName("");
      fetchData(); 
    } catch (err) {
      toast.error("Failed to rename category", { id: toastId });
    }
  };

  const handleUploadBanner = async (e) => {
    e.preventDefault();
    if (!newBannerImage) return toast.error("Please select an image");
    const formData = new FormData();
    formData.append("image", newBannerImage);
    formData.append("title", "Promo Banner");
    formData.append("link", newBannerLink); 
    
    try { 
      await API.post("/banners", formData, { headers: { "Content-Type": "multipart/form-data" } }); 
      toast.success("Banner Uploaded!"); 
      setNewBannerImage(null); 
      setNewBannerLink("All"); 
      fetchData(); 
    } catch (err) { 
      toast.error("Upload failed"); 
    }
  };
  
  const handleDeleteBanner = async (id) => {
    if(!window.confirm("Delete banner?")) return;
    try { await API.delete(`/banners/${id}`); toast.success("Deleted"); fetchData(); } catch(e){}
  };

  const handleStatusUpdate = async (id, status) => {
    try { await API.put(`/orders/${id}`, { status }); toast.success(`Order ${status}`); fetchData(); } catch(e){}
  };
  
  const getFilteredOrders = () => {
    return orders.filter(o => {
      const matchStatus = orderFilter === "All" || o.status === orderFilter;
      const matchDate = selectedDate === "" || (o.createdAt && o.createdAt.startsWith(selectedDate));
      return matchStatus && matchDate;
    });
  };

  const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: 'numeric', month: 'short', year: 'numeric' }) : "N/A";

  const handlePrintOrder = (order) => {
    const printWindow = window.open('', '', 'width=600,height=600');
    const itemsHtml = order.items.map(item => `<tr style="border-bottom: 1px dashed #000;"><td style="padding: 5px; font-size: 12px;">${item.name}</td><td style="padding: 5px; font-size: 12px; text-align: center;">${item.quantity}</td><td style="padding: 5px; font-size: 12px; text-align: right;">₹${item.price * item.quantity}</td></tr>`).join('');
    printWindow.document.write(`<html><head><title>Order #${order._id.slice(-6)}</title><style>body { font-family: 'Courier New', monospace; padding: 20px; width: 300px; margin: 0 auto; } .header { text-align: center; margin-bottom: 10px; } table { width: 100%; border-collapse: collapse; } .total { border-top: 2px solid #000; margin-top: 10px; padding-top: 5px; text-align: right; font-weight: bold; font-size: 16px; }</style></head><body><div class="header"><h2 style="margin:0;">DELIGHT STORE</h2><p style="margin:2px; font-size:12px;">Goverdhan Enclave, Lucknow</p></div><hr/><p style="font-size:12px;"><strong>Order ID:</strong> #${order._id.slice(-6)}</p><p style="font-size:12px;"><strong>Name:</strong> ${order.name}</p><p style="font-size:12px;"><strong>Date:</strong> ${new Date().toLocaleDateString()}</p><hr/><table><thead><tr style="border-bottom: 2px solid #000;"><th style="text-align: left; font-size: 12px;">Item</th><th style="font-size: 12px;">Qty</th><th style="text-align: right; font-size: 12px;">Amt</th></tr></thead><tbody>${itemsHtml}</tbody></table><div class="total">Total: ₹${order.total}</div><hr/><p style="text-align:center; font-size:12px;">Thank you!</p></body></html>`);
    printWindow.document.close(); printWindow.print();
  };

  if (loading) return <div className="flex justify-center items-center h-screen font-bold text-gray-500">Loading Dashboard...</div>;

  return (
    <div className="flex h-screen bg-gray-50 font-sans">
      <Toaster position="top-right" />

      {/* SIDEBAR */}
      <div className="w-64 bg-white border-r border-gray-200 hidden md:flex flex-col z-10">
        <div className="p-6 border-b border-gray-100">
          <h1 className="text-2xl font-extrabold text-green-600 tracking-tight">Delight<span className="text-gray-800">Admin</span></h1>
        </div>
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {['dashboard', 'strategist', 'products', 'combos', 'orders', 'categories', 'banners', 'promos'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-colors ${
                activeTab === tab ? "bg-green-50 text-green-700 font-bold shadow-sm" : "text-gray-500 hover:bg-gray-50"
              }`}
            >
              {/* 🟢 WHATSAPP STYLE RED BADGE ON SIDEBAR */}
              <div className="flex justify-between items-center w-full">
                <span>{tab === 'strategist' ? "🧠 Strategist" : tab.charAt(0).toUpperCase() + tab.slice(1)}</span>
                {tab === 'orders' && unreadOrders > 0 && (
                  <span className="bg-red-500 text-white text-[11px] font-black px-2 py-0.5 rounded-full animate-pulse shadow-sm">
                    {unreadOrders}
                  </span>
                )}
              </div>
            </button>
          ))}
        </nav>
        
        <div className="p-4 border-t border-gray-100">
           <div className="flex items-center justify-between mb-4 bg-gray-100 p-3 rounded-xl">
             <span className="text-sm font-bold text-gray-600">Store:</span>
             <label className="swap swap-rotate">
               <input type="checkbox" checked={isStoreOpen} onChange={toggleStoreStatus} />
               <span className="swap-on text-2xl">🟢</span>
               <span className="swap-off text-2xl">🔴</span>
             </label>
           </div>
           <button onClick={() => { localStorage.removeItem("token"); navigate("/admin-login"); }} className="w-full px-4 py-2 text-red-600 font-medium hover:bg-red-50 rounded-lg text-left flex items-center gap-2">
            <span>🚪</span> Logout
           </button>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="flex-1 overflow-y-auto relative">
        
        {/* Mobile Header */}
        <div className="md:hidden bg-white p-4 flex justify-between items-center border-b sticky top-0 z-20 shadow-sm">
           <h1 className="font-bold text-lg text-green-700">DelightAdmin</h1>
           <div className="flex items-center gap-2">
              <button onClick={toggleStoreStatus} className="btn btn-sm btn-circle btn-ghost">{isStoreOpen ? "🟢" : "🔴"}</button>
              
              {/* 🟢 WHATSAPP STYLE RED BADGE ON MOBILE DROPDOWN */}
              <select className="select select-sm select-bordered font-bold text-gray-700" onChange={(e) => setActiveTab(e.target.value)} value={activeTab}>
                <option value="dashboard">Dashboard</option>
                <option value="strategist">🧠 Strategist</option> 
                <option value="products">Products</option>
                <option value="combos">Combos</option> 
                <option value="orders">Orders {unreadOrders > 0 ? `(🔴 ${unreadOrders})` : ""}</option>
                <option value="categories">Categories</option>
                <option value="banners">Banners</option>
                <option value="promos">Promos</option> 
              </select>
           </div>
        </div>

        <div className="p-4 md:p-8 max-w-7xl mx-auto pb-32">
          
          {/* DASHBOARD VIEW */}
          {activeTab === "dashboard" && (
            <div className="space-y-6 animate-in fade-in duration-500">
              <div className="flex justify-between items-center">
                 <h2 className="text-2xl font-bold text-gray-800">Business Overview</h2>
                 <div className={`px-4 py-1 rounded-full text-sm font-bold border ${isStoreOpen ? "bg-green-100 text-green-700 border-green-200" : "bg-red-100 text-red-700 border-red-200"}`}>
                   {isStoreOpen ? "● Store Open" : "● Store Closed"}
                 </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                  <div className="text-gray-500 text-sm font-medium">Total Revenue</div>
                  <div className="text-3xl font-extrabold text-green-600 mt-2">₹{totalRevenue.toLocaleString()}</div>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                  <div className="text-gray-500 text-sm font-medium">Active Orders</div>
                  <div className="text-3xl font-extrabold text-blue-600 mt-2">{orders.length}</div>
                  <div className="text-xs text-orange-500 font-bold mt-1">{pendingOrdersCount} Pending</div>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                  <div className="text-gray-500 text-sm font-medium">Inventory Alert</div>
                  <div className="text-3xl font-extrabold text-red-500 mt-2">{lowStockCount}</div>
                  <div className="text-xs text-gray-400 mt-1">Items Low Stock</div>
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                  <h3 className="font-bold text-gray-700 mb-4">Recent Sales Trend</h3>
                  <div className="h-64 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={salesData}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="name" tick={{fontSize: 12}} /><YAxis tick={{fontSize: 12}} /><Tooltip /><Bar dataKey="amount" fill="#16a34a" radius={[4, 4, 0, 0]} barSize={30} /></BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                  <h3 className="font-bold text-gray-700 mb-4">Inventory Distribution</h3>
                  <div className="h-64 w-full flex justify-center">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart><Pie data={categoryData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">{categoryData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}</Pie><Tooltip /><Legend verticalAlign="bottom" height={36} iconType="circle" /></PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 🧠 AI STRATEGIST VIEW */}
          {activeTab === "strategist" && (
            <div className="animate-in fade-in duration-500 space-y-8">
              <div className="mb-2">
                <h2 className="text-3xl font-black text-gray-900 tracking-tight">AI Merchandising Strategist</h2>
                <p className="text-gray-500 font-medium mt-1">Automated insights based on customer behavior and inventory data.</p>
              </div>

              {/* SECTION 1: EXPIRY ALERTS */}
              <div className="bg-red-50 border border-red-100 rounded-3xl p-6">
                 <h3 className="text-xl font-bold text-red-700 flex items-center gap-2 mb-4">🚨 Urgent: Approaching Expiry</h3>
                 {aiInsights.expiryAlerts.length === 0 ? (
                   <div className="text-red-400 font-medium">Your inventory is safe. No items expiring within 10 days.</div>
                 ) : (
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     {aiInsights.expiryAlerts.map(p => (
                       <div key={p._id} className="bg-white p-4 rounded-xl shadow-sm flex justify-between items-center border border-red-100">
                         <div>
                           <div className="font-bold text-gray-900">{p.name}</div>
                           <div className="text-sm text-gray-500">Stock: <span className="font-bold">{p.stock}</span></div>
                           <div className="text-xs font-bold text-red-500 mt-1">Expires in {p.daysToExpiry} days!</div>
                         </div>
                         <button onClick={() => openProductModal(p)} className="btn btn-sm bg-red-100 text-red-700 hover:bg-red-200 border-none shadow-none">Discount</button>
                       </div>
                     ))}
                   </div>
                 )}
              </div>

              {/* SECTION 2: PRICE CORRECTIONS */}
              <div className="bg-orange-50 border border-orange-100 rounded-3xl p-6">
                 <h3 className="text-xl font-bold text-orange-700 flex items-center gap-2 mb-4">📉 Price Correction Needed</h3>
                 <p className="text-sm text-orange-600 mb-4">Customers are viewing these items frequently, but dropping off because the price might be too high.</p>
                 {aiInsights.priceCorrections.length === 0 ? (
                   <div className="text-orange-400 font-medium">Your pricing is highly optimized right now.</div>
                 ) : (
                   <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                     {aiInsights.priceCorrections.map(p => (
                       <div key={p._id} className="bg-white p-4 rounded-xl shadow-sm border border-orange-100 flex flex-col">
                         <div className="font-bold text-gray-900 line-clamp-1">{p.name}</div>
                         <div className="flex gap-4 mt-2 mb-3 bg-gray-50 p-2 rounded-lg text-xs font-medium">
                           <div className="flex flex-col"><span className="text-gray-400">Views</span><span className="text-lg font-black text-gray-700">{p.viewCount}</span></div>
                           <div className="flex flex-col"><span className="text-gray-400">Sales</span><span className="text-lg font-black text-gray-700">{p.soldCount || 0}</span></div>
                           <div className="flex flex-col"><span className="text-gray-400">Conv. Rate</span><span className="text-lg font-black text-red-500">{(p.conversionRate * 100).toFixed(1)}%</span></div>
                         </div>
                         <div className="flex justify-between items-end mt-auto">
                           <div className="flex flex-col">
                             <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Suggested Price</span>
                             <span className="text-xl font-extrabold text-orange-600">₹{p.suggestedPrice}</span>
                             <span className="text-xs text-gray-400 line-through">₹{p.price}</span>
                           </div>
                           <button onClick={() => { openProductModal(p); }} className="btn btn-sm bg-orange-100 text-orange-800 hover:bg-orange-200 border-none">Fix Price</button>
                         </div>
                       </div>
                     ))}
                   </div>
                 )}
              </div>

              {/* SECTION 3: TRENDING / COMBO FUEL */}
              <div className="bg-green-50 border border-green-100 rounded-3xl p-6">
                 <h3 className="text-xl font-bold text-green-800 flex items-center gap-2 mb-4">📈 Top Performers (Combo Fuel)</h3>
                 <p className="text-sm text-green-700 mb-4">These items are flying off the shelves. Anchor your Smart Combos around these products!</p>
                 {aiInsights.trending.length === 0 ? (
                   <div className="text-green-600 font-medium">Waiting for more sales data to calculate trends...</div>
                 ) : (
                   <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
                     {aiInsights.trending.slice(0, 10).map(p => (
                       <div key={p._id} className="min-w-[150px] bg-white p-3 rounded-xl shadow-sm border border-green-100 text-center">
                         <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full mb-2 p-1 overflow-hidden">
                           <img src={p.image} className="w-full h-full object-cover rounded-full" alt="" />
                         </div>
                         <div className="font-bold text-sm text-gray-900 line-clamp-1">{p.name}</div>
                         <div className="text-green-600 font-black text-lg mt-1">{p.soldCount} Sold</div>
                       </div>
                     ))}
                   </div>
                 )}
              </div>
            </div>
          )}

          {/* 📦 PRODUCTS VIEW */}
          {activeTab === "products" && (
            <div className="animate-in fade-in duration-300">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <h2 className="text-2xl font-bold text-gray-800">Products ({displayedProducts.length})</h2>
                
                {/* 🟢 FIXED: Add Product button is perfectly visible, bold, and never shrinks */}
                <div className="flex flex-wrap w-full md:w-auto gap-2 items-center">
                  <div className="relative flex-1 min-w-[150px] md:min-w-[200px]">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 text-sm">🔍</span>
                    <input type="text" placeholder="Search..." value={productSearch} onChange={(e) => setProductSearch(e.target.value)} className="input input-sm w-full pl-9 bg-white border border-gray-200 rounded-lg outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-all font-medium" />
                  </div>
                  <button onClick={() => setShowLowStock(!showLowStock)} className={`btn btn-sm shadow-sm ${showLowStock ? 'btn-error text-white' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}>⚠️ Low</button>
                  <button onClick={handleExportCSV} className="btn btn-sm bg-white border-gray-200 text-gray-600 hover:bg-gray-50 shadow-sm hidden md:inline-flex">📥 CSV</button>
                  <button 
                    onClick={() => openProductModal()} 
                    className="btn btn-sm bg-green-600 hover:bg-green-700 text-white border-none shadow-lg shrink-0 whitespace-nowrap w-full sm:w-auto mt-2 sm:mt-0"
                  >
                    + Add Product
                  </button>
                </div>
              </div>

              {selectedProducts.length > 0 && (
                <div className="fixed bottom-4 left-4 right-4 md:left-72 md:right-8 z-50 animate-in slide-in-from-bottom-5">
                  <div className="bg-gray-900 border border-gray-700 text-white px-5 py-4 rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.5)] flex flex-col md:flex-row items-center justify-between gap-4 max-w-5xl mx-auto backdrop-blur-md bg-opacity-95">
                    
                    <div className="flex items-center gap-3">
                      <span className="bg-blue-600 text-white font-black w-8 h-8 flex items-center justify-center rounded-full text-sm">
                        {selectedProducts.length}
                      </span>
                      <span className="font-bold text-sm text-gray-200 tracking-wide uppercase">Items Selected</span>
                    </div>

                    <div className="flex flex-wrap items-center justify-center gap-2">
                       <div className="flex items-center gap-1 bg-gray-800 rounded-lg p-1 border border-gray-600">
                         <select 
                           className="select select-sm bg-transparent text-white border-none focus:ring-0 w-32 font-medium"
                           value={bulkCategory}
                           onChange={(e) => setBulkCategory(e.target.value)}
                         >
                           <option value="" className="text-gray-800">Move to...</option>
                           {categories.map(c => <option key={c._id} value={c.name} className="text-gray-800">{c.name}</option>)}
                         </select>
                         <button onClick={() => handleBulkAction('changeCategory')} className="btn btn-sm btn-primary text-white shadow-sm px-4">Move</button>
                       </div>

                       <div className="h-6 w-px bg-gray-700 mx-1 hidden md:block"></div>

                       <button onClick={() => handleBulkAction('outOfStock')} className="btn btn-sm bg-gray-800 border-gray-600 text-yellow-500 hover:bg-gray-700 hover:border-gray-500">⚡ Out of Stock</button>
                       <button onClick={() => handleBulkAction('toggleVisibility')} className="btn btn-sm bg-gray-800 border-gray-600 text-blue-400 hover:bg-gray-700 hover:border-gray-500">👁️ Toggle Viz</button>
                       <button onClick={() => handleBulkAction('delete')} className="btn btn-sm bg-red-900 border-red-700 text-red-200 hover:bg-red-800 hover:text-white">🗑️ Delete</button>
                       <button onClick={() => setSelectedProducts([])} className="btn btn-sm btn-ghost text-gray-400 hover:text-white ml-1">✕ Cancel</button>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider border-b border-gray-200">
                      <tr>
                        <th className="p-4 w-10"><input type="checkbox" className="checkbox checkbox-sm" checked={displayedProducts.length > 0 && selectedProducts.length === displayedProducts.length} onChange={(e) => { if (e.target.checked) setSelectedProducts(displayedProducts.map(p => p._id)); else setSelectedProducts([]); }} /></th>
                        <th className="p-4">Product</th>
                        <th className="p-4">Category</th>
                        <th className="p-4">Price</th>
                        <th className="p-4">Stats</th>
                        <th className="p-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {displayedProducts.length === 0 ? (
                        <tr><td colSpan="6" className="p-8 text-center text-gray-400 font-medium">No products match your filters.</td></tr>
                      ) : (
                        displayedProducts.map(p => (
                          <tr key={p._id} className={`hover:bg-gray-50 transition-colors group ${p.isVisible === false ? 'opacity-50 grayscale' : ''}`}>
                            <td className="p-4"><input type="checkbox" className="checkbox checkbox-sm" checked={selectedProducts.includes(p._id)} onChange={(e) => { if (e.target.checked) setSelectedProducts([...selectedProducts, p._id]); else setSelectedProducts(selectedProducts.filter(id => id !== p._id)); }} /></td>
                            <td className="p-4 flex items-center gap-3">
                              <div className="relative">
                                 <img src={p.image} alt="" className="w-10 h-10 rounded-lg object-cover border bg-gray-100" />
                                 {p.isVisible === false && <div className="absolute inset-0 bg-black/20 rounded-lg flex items-center justify-center text-xs">❌</div>}
                              </div>
                              <div>
                                <div className="font-bold text-gray-800 text-sm">{p.name}</div>
                                {p.isBestSeller && <span className="text-[10px] bg-yellow-100 text-yellow-700 px-1 rounded border border-yellow-200">Best Seller</span>}
                              </div>
                            </td>
                            <td className="p-4 text-sm text-gray-500">{p.category}</td>
                            <td className="p-4 text-sm">
                               <div className="font-bold text-green-700">₹{p.price}</div>
                               <div className={`text-xs ${p.stock < 5 ? "text-red-500 font-bold" : "text-gray-400"}`}>Stock: {p.stock}</div>
                            </td>
                            <td className="p-4">
                               <div className="flex flex-col gap-0.5 text-xs font-mono">
                                 <span className="text-gray-500">👁 {p.viewCount || 0}</span>
                                 <span className="text-blue-600 font-bold">🛒 {p.soldCount || 0}</span>
                               </div>
                            </td>
                            <td className="p-4 text-right space-x-2">
                              <button onClick={() => toggleVisibility(p._id)} className="btn btn-xs btn-outline" title={p.isVisible !== false ? "Hide from Shop" : "Show in Shop"}>{p.isVisible !== false ? "👁️" : "🙈"}</button>
                              {p.stock > 0 && <button onClick={() => handleMarkOutOfStock(p._id)} className="btn btn-xs btn-outline btn-error" title="Mark Out of Stock">⚡</button>}
                              <button onClick={() => openProductModal(p)} className="btn btn-xs btn-outline">✏️</button>
                              <button onClick={() => handleDeleteProduct(p._id)} className="btn btn-xs btn-ghost text-gray-400 hover:text-red-600">🗑️</button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* 🎁 SMART COMBOS VIEW */}
          {activeTab === "combos" && (
            <div className="animate-in fade-in duration-300">
              {/* 🟢 FIXED: Create Combo button is highly visible */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                <h2 className="text-2xl font-bold text-gray-800">Smart Combos ({combos.length})</h2>
                <button onClick={() => setShowComboModal(true)} className="btn bg-green-600 hover:bg-green-700 text-white border-none shadow-lg gap-2 shrink-0 w-full sm:w-auto">
                  + Create Combo
                </button>
              </div>

              {combos.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-300 text-gray-500">
                   <div className="text-5xl mb-3">🎁</div>
                   <p className="font-bold text-lg">No Combos Yet</p>
                   <p className="text-sm">Group products together to increase your average order value!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {combos.map(combo => (
                    <div key={combo._id} className={`bg-white rounded-2xl border ${combo.isVisible ? 'border-green-200 shadow-sm' : 'border-gray-200 opacity-70 grayscale'} overflow-hidden`}>
                      <div className="relative h-40 bg-gray-100">
                        <img src={combo.image} alt={combo.name} className="w-full h-full object-cover" />
                        {!combo.isVisible && <div className="absolute inset-0 bg-black/40 flex items-center justify-center text-white font-bold tracking-widest">HIDDEN</div>}
                        <div className="absolute top-2 right-2 bg-green-600 text-white text-xs font-bold px-2 py-1 rounded-md shadow-md">
                           SAVE ₹{combo.originalPrice - combo.comboPrice}
                        </div>
                      </div>
                      <div className="p-5">
                         <h3 className="font-extrabold text-xl text-gray-900 mb-1">{combo.name}</h3>
                         <div className="flex items-center gap-2 mb-3">
                            <span className="text-2xl font-black text-green-700">₹{combo.comboPrice}</span>
                            <span className="text-sm text-gray-400 line-through font-medium">₹{combo.originalPrice}</span>
                         </div>
                         
                         <div className="bg-gray-50 rounded-xl p-3 mb-4 border border-gray-100">
                            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-2">Bundle Includes:</span>
                            <ul className="space-y-1">
                              {combo.items.map((item, idx) => (
                                <li key={idx} className="text-sm font-medium text-gray-700 flex justify-between">
                                  <span className="truncate pr-2">• {item.product?.name || "Deleted Product"}</span>
                                  <span className="text-gray-400 font-bold bg-gray-200 px-1.5 rounded text-xs">x{item.quantity}</span>
                                </li>
                              ))}
                            </ul>
                         </div>

                         <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                            <button onClick={() => toggleComboVisibility(combo._id)} className="btn btn-sm btn-outline text-gray-600 hover:bg-gray-100">
                               {combo.isVisible ? "🙈 Hide" : "👁️ Show"}
                            </button>
                            <button onClick={() => handleDeleteCombo(combo._id)} className="btn btn-sm btn-ghost text-red-500 hover:bg-red-50">🗑️ Delete</button>
                         </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 🧾 ORDERS VIEW */}
          {activeTab === "orders" && (
            <div className="animate-in fade-in duration-300">
              <div className="flex flex-col md:flex-row justify-between items-end mb-6 gap-4">
                <h2 className="text-2xl font-bold text-gray-800">Orders ({getFilteredOrders().length})</h2>
                <div className="flex flex-wrap gap-2 bg-white p-2 rounded-xl shadow-sm border border-gray-200">
                  <div className="flex bg-gray-100 p-1 rounded-lg">
                    {["All", "Pending", "Delivered", "Cancelled"].map(f => (
                      <button key={f} onClick={() => setOrderFilter(f)} className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${orderFilter === f ? "bg-white shadow text-gray-900" : "text-gray-500 hover:text-gray-700"}`}>{f}</button>
                    ))}
                  </div>
                  <input type="date" className="input input-sm input-bordered rounded-lg text-xs" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
                  {selectedDate && <button onClick={() => setSelectedDate("")} className="btn btn-xs btn-circle btn-ghost text-red-500">✕</button>}
                </div>
              </div>

              <div className="space-y-4">
                {getFilteredOrders().length === 0 ? (
                  <div className="text-center py-10 bg-white rounded-xl border border-dashed text-gray-400">No orders found matching filters.</div>
                ) : (
                  getFilteredOrders().map(o => (
                    <div key={o._id} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <span className="font-bold text-lg text-gray-800">{o.name}</span>
                          <span className="text-[10px] font-mono bg-gray-100 text-gray-600 px-2 py-1 rounded border border-gray-200">{formatDate(o.createdAt)}</span>
                        </div>
                        <div className="text-sm text-gray-500 flex items-center gap-2">
                           <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs font-bold">#{o._id.slice(-6)}</span>
                           <span>• {o.items.length} Items</span>
                           <span>• <strong className="text-green-700">₹{o.total}</strong></span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 w-full md:w-auto">
                        <select value={o.status} onChange={(e) => handleStatusUpdate(o._id, e.target.value)} className="select select-sm select-bordered w-full md:w-36 font-bold text-xs">
                          <option value="Pending">🕒 Pending</option>
                          <option value="Shipped">🚚 Shipped</option>
                          <option value="Delivered">✅ Delivered</option>
                          <option value="Cancelled">❌ Cancelled</option>
                        </select>
                        <button onClick={() => setSelectedOrder(o)} className="btn btn-sm btn-circle btn-ghost border border-gray-200">👁️</button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* CATEGORIES VIEW */}
          {activeTab === "categories" && (
             <div className="max-w-2xl animate-in fade-in duration-300">
               <h2 className="text-2xl font-bold text-gray-800 mb-6">Manage Categories</h2>
               <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                  {/* 🟢 FIXED: Add Category button highly visible and won't squish */}
                  <form onSubmit={handleAddCategory} className="flex gap-3 mb-6">
                    <input className="input input-bordered w-full" placeholder="New category..." value={newCategory} onChange={e => setNewCategory(e.target.value)} />
                    <button className="btn bg-green-600 hover:bg-green-700 text-white border-none shrink-0 px-6">+ Add Category</button>
                  </form>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {categories.map(c => (
                      <div key={c._id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border border-gray-100">
                        
                        {editingCategory === c._id ? (
                           <form onSubmit={submitEditCategory} className="flex gap-2 w-full mr-2">
                             <input className="input input-sm input-bordered w-full" value={editingCategoryName} onChange={(e) => setEditingCategoryName(e.target.value)} autoFocus />
                             <button type="submit" className="btn btn-sm btn-success text-white">Save</button>
                             <button type="button" onClick={() => setEditingCategory(null)} className="btn btn-sm btn-ghost">Cancel</button>
                           </form>
                        ) : (
                           <>
                             <span className="font-medium text-gray-700">{c.name}</span>
                             <div className="flex gap-1">
                               <button onClick={() => { setEditingCategory(c._id); setEditingCategoryName(c.name); }} className="btn btn-xs btn-circle btn-ghost text-blue-500">✏️</button>
                               <button onClick={() => handleDeleteCategory(c._id)} className="btn btn-xs btn-circle btn-ghost text-red-500">✕</button>
                             </div>
                           </>
                        )}
                        
                      </div>
                    ))}
                  </div>
               </div>
             </div>
          )}
          
          {activeTab === "banners" && (
            <div className="max-w-4xl animate-in fade-in duration-300">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Store Banners</h2>
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 mb-8">
                
                <form onSubmit={handleUploadBanner} className="flex flex-col md:flex-row gap-4 items-end">
                  <div className="w-full flex-1">
                     <label className="label text-xs font-bold text-gray-500 p-0 mb-1">Banner Image</label>
                     <input type="file" className="file-input file-input-bordered w-full" onChange={(e) => setNewBannerImage(e.target.files[0])} accept="image/*" />
                  </div>
                  <div className="w-full flex-1">
                     <label className="label text-xs font-bold text-gray-500 p-0 mb-1">Target Category (Optional)</label>
                     <select className="select select-bordered w-full" value={newBannerLink} onChange={(e) => setNewBannerLink(e.target.value)}>
                        <option value="All">All (Home Feed)</option>
                        {categories.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
                     </select>
                  </div>
                  <button className="btn bg-green-600 hover:bg-green-700 text-white border-none w-full md:w-auto">Upload</button>
                </form>

              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {banners.map((banner) => (
                   <div key={banner._id} className="relative group rounded-xl overflow-hidden shadow-sm border border-gray-200">
                      <img src={banner.image} alt="Banner" className="w-full h-48 object-cover" />
                      
                      <div className="absolute top-2 left-2 bg-black/70 text-white text-xs font-bold px-2 py-1 rounded">
                        Target: {banner.link || "All"}
                      </div>
                      
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => handleDeleteBanner(banner._id)} className="btn btn-error text-white font-bold">🗑️ Delete</button>
                      </div>
                   </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === "promos" && <div className="animate-in fade-in duration-300"><AdminPromos /></div>}
        </div>
      </div>

      {/* 📦 ADD/EDIT PRODUCT MODAL */}
      {showProductModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1000] flex justify-center items-center p-4">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-4 border-b flex justify-between items-center bg-gray-50 flex-none">
              <h3 className="font-bold text-lg">{editId ? "Edit Product" : "Add New Product"}</h3>
              <button onClick={() => setShowProductModal(false)} className="btn btn-sm btn-circle btn-ghost">✕</button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <form id="productForm" onSubmit={handleSubmitProduct} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-control"><label className="label text-xs font-bold text-gray-500">Name</label><input className="input input-bordered" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required /></div>
                <div className="form-control"><label className="label text-xs font-bold text-gray-500">Category</label><select className="select select-bordered" value={form.category} onChange={e => setForm({...form, category: e.target.value})} required><option value="">Select</option>{categories.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}</select></div>
                <div className="form-control"><label className="label text-xs font-bold text-gray-500">MRP</label><input type="number" className="input input-bordered" value={form.mrp} onChange={e => setForm({...form, mrp: e.target.value})} required /></div>
                <div className="form-control"><label className="label text-xs font-bold text-gray-500">Selling Price</label><input type="number" className="input input-bordered" value={form.price} onChange={e => setForm({...form, price: e.target.value})} required /></div>
                
                {/* 🟢 AI DESCRIPTION FIELD RESTORED */}
                <div className="form-control col-span-1 md:col-span-2">
                  <div className="flex justify-between items-end mb-1">
                    <label className="label text-xs font-bold text-gray-500 p-0">Description (Optional)</label>
                    <button type="button" onClick={handleGenerateDescription} className="btn btn-xs bg-purple-100 text-purple-700 hover:bg-purple-200 border-none">✨ Auto-Write</button>
                  </div>
                  <textarea className="textarea textarea-bordered h-20" placeholder="Product details..." value={form.description} onChange={e => setForm({...form, description: e.target.value})}></textarea>
                </div>

                {/* AI FIELDS */}
                <div className="form-control"><label className="label text-xs font-bold text-gray-500">Cost Price (Secret Margin)</label><input type="number" className="input input-bordered" value={form.costPrice} onChange={e => setForm({...form, costPrice: e.target.value})} /></div>
                <div className="form-control"><label className="label text-xs font-bold text-gray-500">Expiry Date (For Auto-Discount)</label><input type="date" className="input input-bordered" value={form.expiryDate} onChange={e => setForm({...form, expiryDate: e.target.value})} /></div>
                
                <div className="form-control"><label className="label text-xs font-bold text-gray-500">Stock</label><input type="number" className="input input-bordered" value={form.stock} onChange={e => setForm({...form, stock: e.target.value})} required /></div>
                <div className="form-control flex flex-col gap-2 pt-4">
                   <label className="cursor-pointer flex items-center gap-2"><input type="checkbox" className="checkbox checkbox-warning" checked={form.isBestSeller} onChange={e => setForm({...form, isBestSeller: e.target.checked})} /><span className="font-bold">🏆 Best Seller?</span></label>
                   <label className="cursor-pointer flex items-center gap-2"><input type="checkbox" className="checkbox checkbox-primary" checked={form.isVisible} onChange={e => setForm({...form, isVisible: e.target.checked})} /><span className="font-bold">👁️ Visible in Shop?</span></label>
                </div>
                <div className="col-span-1 md:col-span-2"><label className="label text-xs font-bold text-gray-500">Image</label><input type="file" className="file-input file-input-bordered w-full" onChange={e => setForm({...form, image: e.target.files[0]})} /></div>
              </form>
            </div>
            <div className="p-4 border-t bg-gray-50 flex-none z-10">
               <button form="productForm" type="submit" className="btn bg-green-600 hover:bg-green-700 w-full text-white border-none shadow-md text-lg font-bold">{editId ? "Update Product" : "Create Product +"}</button>
            </div>
          </div>
        </div>
      )}

      {/* 🎁 CREATE COMBO MODAL */}
      {showComboModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1000] flex justify-center items-center p-4">
          <div className="bg-white w-full max-w-4xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95">
            <div className="p-4 border-b flex justify-between items-center bg-gray-50 flex-none">
              <h3 className="font-extrabold text-xl">Create a Smart Combo</h3>
              <button onClick={() => setShowComboModal(false)} className="btn btn-sm btn-circle btn-ghost bg-gray-200">✕</button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 bg-white">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                
                {/* Left Column: Combo Details */}
                <div className="space-y-4">
                   <h4 className="font-bold text-gray-800 border-b pb-2">1. Bundle Details</h4>
                   <div className="form-control"><label className="label text-xs font-bold text-gray-500">Combo Name</label><input className="input input-bordered bg-gray-50" placeholder="e.g. Movie Night Snack Pack" value={comboForm.name} onChange={e => setComboForm({...comboForm, name: e.target.value})} /></div>
                   <div className="form-control"><label className="label text-xs font-bold text-gray-500">Short Description</label><textarea className="textarea textarea-bordered bg-gray-50" placeholder="A great mix for the weekend..." value={comboForm.description} onChange={e => setComboForm({...comboForm, description: e.target.value})}></textarea></div>
                   
                   {/* 🟢 NEW 3-COLUMN PSYCHOLOGICAL PRICING GRID */}
                   <div className="grid grid-cols-3 gap-3">
                      <div className="form-control">
                        <label className="label text-[10px] font-bold text-gray-500 uppercase tracking-wide">Total MRP</label>
                        <div className="input input-bordered input-sm bg-gray-200 flex items-center font-bold text-gray-500 line-through decoration-red-500">
                          ₹{calculatedMRP}
                        </div>
                      </div>
                      <div className="form-control">
                        <label className="label text-[10px] font-bold text-blue-600 uppercase tracking-wide">Bought Separately</label>
                        <div className="input input-bordered input-sm bg-blue-50 flex items-center font-bold text-blue-800">
                          ₹{calculatedSellingPrice}
                        </div>
                      </div>
                      <div className="form-control">
                        <label className="label text-[10px] font-bold text-green-700 uppercase tracking-wide">Combo Price</label>
                        <input type="number" className="input input-bordered input-sm border-green-500 bg-green-50 text-green-800 font-bold" placeholder={`< ₹${calculatedSellingPrice}`} value={comboForm.comboPrice} onChange={e => setComboForm({...comboForm, comboPrice: e.target.value})} />
                      </div>
                   </div>
                   
                   <div className="form-control mt-2"><label className="label text-xs font-bold text-gray-500">Promotional Image</label><input type="file" className="file-input file-input-bordered file-input-md w-full bg-gray-50" onChange={e => setComboForm({...comboForm, image: e.target.files[0]})} /></div>
                </div>

                {/* Right Column: Build the Bundle */}
                <div className="space-y-4 bg-gray-50 p-5 rounded-2xl border border-gray-100">
                   <h4 className="font-bold text-gray-800 border-b border-gray-200 pb-2">2. Select Products</h4>
                   
                   <div className="flex gap-2">
                     <select className="select select-bordered flex-1 bg-white font-medium text-sm" value={comboSelectedProduct} onChange={e => setComboSelectedProduct(e.target.value)}>
                       <option value="">Select an inventory item...</option>
                       {products.map(p => <option key={p._id} value={p._id}>{p.name} (₹{p.price})</option>)}
                     </select>
                     <input type="number" min="1" className="input input-bordered w-20 bg-white text-center font-bold" value={comboSelectedQty} onChange={e => setComboSelectedQty(e.target.value)} />
                     <button type="button" onClick={handleAddComboItem} className="btn bg-gray-800 hover:bg-gray-900 text-white px-6">Add</button>
                   </div>

                   <div className="mt-4">
                     {comboForm.items.length === 0 ? (
                       <div className="text-center py-8 text-gray-400 text-sm font-medium border-2 border-dashed border-gray-200 rounded-xl bg-white">Bundle is empty. Add products above!</div>
                     ) : (
                       <div className="space-y-2 max-h-[250px] overflow-y-auto pr-2">
                         {comboForm.items.map((item, index) => (
                           <div key={index} className="flex justify-between items-center bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                             <div className="flex items-center gap-3">
                               <span className="bg-gray-100 text-gray-600 font-black px-2.5 py-1 rounded-lg text-xs">x{item.quantity}</span>
                               <div>
                                 <div className="font-bold text-sm text-gray-800">{item.name}</div>
                                 <div className="text-xs text-gray-500 font-medium">₹{item.price * item.quantity} (₹{item.price} each)</div>
                               </div>
                             </div>
                             <button onClick={() => removeComboItem(index)} className="btn btn-circle btn-xs btn-ghost text-red-500 bg-red-50 hover:bg-red-100">✕</button>
                           </div>
                         ))}
                       </div>
                     )}
                   </div>
                </div>
              </div>
            </div>

            <div className="p-4 border-t bg-white flex-none z-10">
               <button onClick={handleSubmitCombo} className="btn bg-green-600 hover:bg-green-700 w-full text-white border-none shadow-lg text-lg font-bold">Publish Combo 🚀</button>
            </div>
          </div>
        </div>
      )}

      {/* 🧾 ORDER DETAILS MODAL */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
           <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
              <button onClick={() => setSelectedOrder(null)} className="absolute top-4 right-4 btn btn-circle btn-ghost btn-sm">✕</button>
              <h2 className="text-xl font-bold mb-4">Order Details</h2>
              <div className="space-y-2 text-sm bg-blue-50 p-4 rounded-lg border border-blue-100 mb-4">
                 <p><strong>Customer:</strong> {selectedOrder.name}</p>
                 <p><strong>Phone:</strong> {selectedOrder.phone}</p>
                 <p><strong>Address:</strong> {selectedOrder.address}</p>
                 <p><strong>Date:</strong> {formatDate(selectedOrder.createdAt)}</p>
              </div>
              <div className="max-h-60 overflow-y-auto space-y-3">
                {selectedOrder.items.map((i, idx) => (
                    <div key={idx} className="flex justify-between items-center border-b border-gray-100 pb-2">
                       <div className="flex items-center gap-3">
                          {i.image && <img src={i.image} className="w-10 h-10 rounded bg-gray-100 object-cover" alt="" />}
                          <div><div className="font-bold text-sm">{i.name}</div><div className="text-xs text-gray-500">{i.quantity} x ₹{i.price}</div></div>
                       </div>
                       <div className="font-bold text-gray-700">₹{i.price * i.quantity}</div>
                    </div>
                ))}
              </div>
              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-100">
                <div className="text-xl font-extrabold text-green-700">Total: ₹{selectedOrder.total}</div>
                <button onClick={() => handlePrintOrder(selectedOrder)} className="btn btn-outline btn-sm gap-2">🖨️ Print Bill</button>
              </div>
           </div>
        </div>
      )}
    </div>
  );
};

export default Admin;